<template>
  <div class="page-card distribution-page">
    <div class="page-head">
      <h2 class="page-title">分销中心</h2>
      <el-button type="primary" @click="fetchAll">刷新</el-button>
    </div>

    <div class="metric-grid">
      <el-card v-for="item in metrics" :key="item.label" shadow="never">
        <div class="metric-value">{{ item.value }}</div>
        <div class="metric-label">{{ item.label }}</div>
      </el-card>
    </div>

    <el-tabs v-model="activeTab" class="distribution-tabs">
      <el-tab-pane label="规则配置" name="rules">
        <div class="section-actions rule-toolbar">
          <div class="rule-filters">
            <el-select v-model="ruleFilters.status" clearable placeholder="状态" size="small">
              <el-option label="启用" :value="2" />
              <el-option label="停用" :value="1" />
            </el-select>
            <el-select v-model="ruleFilters.type" clearable placeholder="类型" size="small">
              <el-option v-for="item in ruleTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-button size="small" @click="resetRuleFilters">重置</el-button>
          </div>
          <el-button type="primary" @click="openRule()">新增规则</el-button>
        </div>
        <el-table :data="filteredRules" border>
          <el-table-column prop="ruleName" label="规则名称" min-width="170" />
          <el-table-column label="类型" width="150">
            <template slot-scope="{ row }">
              <el-tag size="mini">{{ ruleModeText(row.ruleMode) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="适用角色" width="110">
            <template slot-scope="{ row }">{{ isCommissionMode(row.ruleMode) ? commissionRoleText(row.targetUserRole) : '-' }}</template>
          </el-table-column>
          <el-table-column label="关键配置" min-width="260">
            <template slot-scope="{ row }">{{ ruleSummary(row) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">{{ row.ruleStatus === 2 ? '启用' : '停用' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openRule(row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="推广关系" name="relations">
        <div class="section-actions relation-toolbar">
          <div class="relation-filters">
            <el-select v-model="relationFilters.effectiveStatus" clearable placeholder="有效状态" size="small" @change="fetchRelations">
              <el-option label="已有效" :value="1" />
              <el-option label="未有效" :value="0" />
              <el-option label="异常不计入" :value="2" />
            </el-select>
            <el-select v-model="relationFilters.inviteeSubjectType" clearable placeholder="用户类型" size="small" @change="handleRelationSubjectChange">
              <el-option label="艺人" :value="1" />
              <el-option label="商家" :value="2" />
            </el-select>
            <el-select
              v-model="relationFilters.promoterUserId"
              filterable
              clearable
              placeholder="选择推广用户"
              size="small"
              @change="fetchRelations"
            >
              <el-option
                v-for="item in promoterOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-select
              v-model="relationFilters.inviteeUserId"
              filterable
              clearable
              placeholder="选择被推广用户"
              size="small"
              @change="fetchRelations"
            >
              <el-option
                v-for="item in inviteeOptions"
                :key="item.key"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-button size="small" @click="resetRelationFilters">重置</el-button>
          </div>
        </div>
        <el-table :data="relations" border>
          <el-table-column prop="no" label="关系编号" min-width="170" />
          <el-table-column prop="promoterName" label="推广人姓名" min-width="110" />
          <el-table-column prop="promoterMobile" label="推广人手机号" width="120" />
          <el-table-column prop="inviteeSubjectText" label="用户类型" width="80" />
          <el-table-column prop="inviteeName" label="被推广人名称" min-width="120" />
          <el-table-column prop="inviteeMobile" label="被推广人手机号" width="120" />
          <el-table-column label="关系状态" width="90">
            <template slot-scope="{ row }">
              <el-tag :type="relationStatusTag(row.type)" size="mini">{{ row.typeText || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="有效状态" width="90">
            <template slot-scope="{ row }">
              <el-tag :type="effectiveStatusTag(row.status)" size="mini">{{ row.statusText || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="绑定时间" width="160" />
          <el-table-column prop="remark" label="备注" min-width="120" />
          <el-table-column label="操作" width="220">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openRelationDetail(row)">详情</el-button>
              <el-button type="text" :disabled="row.type === 1" @click="updateRelationStatus(row, 1, '恢复关系')">恢复</el-button>
              <el-button type="text" :disabled="row.type === 2" @click="updateRelationStatus(row, 2, '冻结关系')">冻结</el-button>
              <el-button type="text" class="danger-link" :disabled="row.type === 3" @click="updateRelationStatus(row, 3, '解除关系', true)">解除</el-button>
              <el-button type="text" class="danger-link" :disabled="row.type === 4" @click="updateRelationStatus(row, 4, '标记异常', true)">异常</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="奖励审核" name="rewards">
        <div class="section-actions audit-toolbar">
          <div class="audit-filters">
            <el-select
              v-model="rewardFilters.artistName"
              filterable
              clearable
              placeholder="选择或搜索实名姓名"
              size="small"
            >
              <el-option
                v-for="item in rewardArtistOptions"
                :key="item.key"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-select v-model="rewardFilters.type" clearable placeholder="奖励类型" size="small">
              <el-option v-for="item in rewardTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="rewardFilters.status" clearable placeholder="状态" size="small">
              <el-option v-for="item in rewardStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-button size="small" @click="resetRewardFilters">重置</el-button>
          </div>
        </div>
        <el-table :data="filteredRewards" border>
          <el-table-column prop="no" label="编号" min-width="170" />
          <el-table-column prop="artistName" label="用户姓名" min-width="130" />
          <el-table-column prop="mobile" label="用户手机号" width="130" />
          <el-table-column prop="title" label="奖励类型" width="120" />
          <el-table-column prop="content" label="内容" min-width="160" />
          <el-table-column prop="statusText" label="状态" width="100" />
          <el-table-column prop="createdAt" label="创建时间" width="170" />
          <el-table-column label="操作" width="160">
            <template slot-scope="{ row }">
              <el-button type="text" :disabled="row.status !== 1" @click="approveReward(row)">通过</el-button>
              <el-button type="text" class="danger-link" :disabled="row.status !== 1" @click="rejectReward(row)">驳回</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="兑换审核" name="exchanges">
        <div class="section-actions audit-toolbar">
          <div class="audit-filters">
            <el-select
              v-model="exchangeFilters.artistName"
              filterable
              clearable
              placeholder="选择或搜索实名姓名"
              size="small"
            >
              <el-option
                v-for="item in exchangeArtistOptions"
                :key="item.key"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-select v-model="exchangeFilters.type" clearable placeholder="类型" size="small">
              <el-option v-for="item in rewardTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="exchangeFilters.status" clearable placeholder="状态" size="small">
              <el-option v-for="item in exchangeStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-button size="small" @click="resetExchangeFilters">重置</el-button>
          </div>
        </div>
        <el-table :data="filteredExchanges" border>
          <el-table-column prop="no" label="编号" min-width="170" />
          <el-table-column prop="artistName" label="用户姓名" min-width="130" />
          <el-table-column prop="mobile" label="用户手机号" width="130" />
          <el-table-column prop="title" label="类型" width="120" />
          <el-table-column prop="content" label="内容" min-width="140" />
          <el-table-column prop="statusText" label="状态" width="100" />
          <el-table-column prop="createdAt" label="申请时间" width="170" />
          <el-table-column label="操作" width="160">
            <template slot-scope="{ row }">
              <el-button type="text" :disabled="row.status !== 1" @click="approveExchange(row)">通过</el-button>
              <el-button type="text" class="danger-link" :disabled="row.status !== 1" @click="rejectExchange(row)">驳回</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="提现审核" name="withdraws">
        <div class="section-actions audit-toolbar">
          <div class="audit-filters">
            <el-select
              v-model="withdrawFilters.artistName"
              filterable
              clearable
              placeholder="选择或搜索实名姓名"
              size="small"
            >
              <el-option
                v-for="item in withdrawArtistOptions"
                :key="item.key"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <el-select v-model="withdrawFilters.status" clearable placeholder="状态" size="small">
              <el-option v-for="item in withdrawStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-button size="small" @click="resetWithdrawFilters">重置</el-button>
          </div>
        </div>
        <el-table :data="filteredWithdraws" border>
          <el-table-column prop="no" label="编号" min-width="170" />
          <el-table-column prop="artistName" label="用户姓名" width="120" />
          <el-table-column prop="mobile" label="用户手机号" width="130" />
          <el-table-column prop="amount" label="金额" width="100" />
          <el-table-column prop="subtitle" label="收款账号" min-width="150" />
          <el-table-column prop="statusText" label="状态" width="120" />
          <el-table-column prop="createdAt" label="申请时间" width="170" />
          <el-table-column label="操作" width="260">
            <template slot-scope="{ row }">
              <el-button type="text" :disabled="row.status !== 1" @click="approveWithdraw(row)">审核通过</el-button>
              <el-button type="text" class="danger-link" :disabled="row.status !== 1" @click="rejectWithdraw(row)">驳回</el-button>
              <el-button type="text" :disabled="row.status !== 4" @click="markPaid(row)">已打款</el-button>
              <el-button type="text" class="danger-link" :disabled="row.status !== 4" @click="markFailed(row)">打款失败</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="优惠券模板" name="templates">
        <div class="section-actions">
          <el-button type="primary" @click="openCoupon()">新增优惠券</el-button>
        </div>
        <el-table :data="couponTemplates" border>
          <el-table-column prop="templateName" label="名称" min-width="160" />
          <el-table-column prop="discountAmount" label="减免金额" width="110" />
          <el-table-column prop="validDays" label="有效天数" width="100" />
          <el-table-column prop="issuedQuantity" label="已发放" width="100" />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">{{ row.templateStatus === 1 ? '启用' : '停用' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openCoupon(row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="券包" name="coupons">
        <el-table :data="userCoupons" border>
          <el-table-column prop="no" label="编号" min-width="170" />
          <el-table-column prop="title" label="券名称" min-width="150" />
          <el-table-column prop="artistName" label="用户真实姓名" min-width="150" />
          <el-table-column prop="statusText" label="状态" width="110" />
          <el-table-column prop="content" label="有效期至" width="170" />
          <el-table-column prop="createdAt" label="领取时间" width="170" />
          <el-table-column prop="remark" label="备注" min-width="160" />
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="推广详情" :visible.sync="relationDetailVisible" width="680px">
      <el-descriptions :column="2" border size="small" class="relation-detail">
        <el-descriptions-item label="关系编号">{{ detailText(relationDetail.no) }}</el-descriptions-item>
        <el-descriptions-item label="推广码">{{ detailText(relationDetail.inviteCode) }}</el-descriptions-item>
        <el-descriptions-item label="推广人姓名">{{ detailText(relationDetail.promoterName) }}</el-descriptions-item>
        <el-descriptions-item label="推广人手机号">{{ detailText(relationDetail.promoterMobile) }}</el-descriptions-item>
        <el-descriptions-item label="推广人用户ID">{{ detailText(relationDetail.promoterUserId) }}</el-descriptions-item>
        <el-descriptions-item label="被推广人类型">{{ detailText(relationDetail.inviteeSubjectText) }}</el-descriptions-item>
        <el-descriptions-item label="被推广人名称">{{ detailText(relationDetail.inviteeName) }}</el-descriptions-item>
        <el-descriptions-item label="被推广人手机号">{{ detailText(relationDetail.inviteeMobile) }}</el-descriptions-item>
        <el-descriptions-item label="被推广人用户ID">{{ detailText(relationDetail.inviteeUserId) }}</el-descriptions-item>
        <el-descriptions-item label="来源渠道">{{ detailText(relationDetail.sourceChannel) }}</el-descriptions-item>
        <el-descriptions-item label="来源页面">{{ detailText(relationDetail.sourcePage) }}</el-descriptions-item>
        <el-descriptions-item label="关系状态">{{ detailText(relationDetail.typeText) }}</el-descriptions-item>
        <el-descriptions-item label="有效状态">{{ detailText(relationDetail.statusText) }}</el-descriptions-item>
        <el-descriptions-item label="绑定时间">{{ detailText(relationDetail.boundAt || relationDetail.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="生效时间">{{ detailText(relationDetail.effectiveAt) }}</el-descriptions-item>
        <el-descriptions-item label="生效原因">{{ detailText(relationDetail.effectiveReason) }}</el-descriptions-item>
        <el-descriptions-item label="风险状态">{{ relationDetail.riskStatus === 2 ? '异常' : '正常' }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ detailText(relationDetail.riskReason || relationDetail.remark) }}</el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button type="primary" @click="relationDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="ruleForm.id ? '编辑规则' : '新增规则'" :visible.sync="ruleVisible" width="680px">
      <el-form :model="ruleForm" label-width="120px">
        <el-divider content-position="left">基础规则</el-divider>
        <el-form-item label="规则名称">
          <el-input v-model="ruleForm.ruleName" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="ruleForm.ruleMode" @change="syncRuleModeDefaults">
            <el-option label="推N返一" :value="1" />
            <el-option label="提现规则" :value="2" />
            <el-option label="订单完成抽成" :value="3" />
            <el-option label="商家曝光充值抽成" :value="4" />
            <el-option label="艺人会员抽成" :value="5" />
          </el-select>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="ruleEnabled" />
        </el-form-item>
        <el-form-item label="奖励自动审核">
          <el-switch
            v-model="ruleAutoAuditEnabled"
            active-text="自动通过"
            inactive-text="人工审核"
          />
        </el-form-item>

        <template v-if="ruleForm.ruleMode === 1">
          <el-divider content-position="left">推N返一</el-divider>
          <el-alert
            class="rule-effective-alert"
            type="warning"
            :closable="false"
            show-icon
            title="保存后仅统计保存时间之后产生的邀请，历史邀请不会触发该规则奖励。"
          />
          <el-form-item label="邀请人数">
            <el-input-number v-model="ruleForm.inviteTargetCount" :min="1" :precision="0" />
          </el-form-item>
          <el-form-item label="执行次数">
            <el-radio-group v-model="rewardLimitMode">
              <el-radio-button label="repeat">可重复</el-radio-button>
              <el-radio-button label="limit">限制次数</el-radio-button>
            </el-radio-group>
            <el-input-number
              v-if="rewardLimitMode === 'limit'"
              v-model="ruleForm.perArtistRewardLimit"
              class="limit-input"
              :min="1"
              :precision="0"
            />
            <span v-if="rewardLimitMode === 'limit'" class="unit">次</span>
          </el-form-item>
          <el-form-item label="有效邀请条件">
            <el-checkbox v-model="needRegister">完成注册</el-checkbox>
            <el-checkbox v-model="needRealname">实名认证</el-checkbox>
            <el-checkbox v-model="needApplication">成功报名</el-checkbox>
            <el-checkbox v-model="needOrder">完成下单</el-checkbox>
            <el-checkbox v-model="needMembership">开通会员</el-checkbox>
          </el-form-item>
          <el-form-item label="奖励类型">
            <el-select v-model="ruleForm.rewardType">
              <el-option label="现金余额" :value="1" />
              <el-option label="会员时长" :value="2" />
              <el-option label="优惠券" :value="3" />
              <el-option label="曝光值" :value="4" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="ruleForm.rewardType === 1" label="现金金额">
            <el-input-number v-model="ruleForm.rewardAmount" :min="0" :precision="2" />
          </el-form-item>
          <el-form-item v-if="ruleForm.rewardType === 2" label="会员天数">
            <el-input-number v-model="ruleForm.rewardMembershipDays" :min="1" :precision="0" />
          </el-form-item>
          <el-form-item v-if="ruleForm.rewardType === 3" label="优惠券">
            <el-select v-model="ruleForm.rewardCouponTemplateId" filterable clearable>
              <el-option v-for="item in couponTemplates" :key="item.id" :label="item.templateName" :value="item.templateNo || String(item.id)" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="ruleForm.rewardType === 4" label="曝光值">
            <el-input-number v-model="ruleForm.rewardExposureValue" :min="1" :precision="0" />
          </el-form-item>
        </template>

        <template v-if="ruleForm.ruleMode === 2">
          <el-divider content-position="left">提现规则</el-divider>
          <el-form-item label="基础门槛">
            <el-input-number v-model="ruleForm.withdrawMinAmount" :min="0" :precision="2" />
            <span class="unit">元</span>
          </el-form-item>
          <el-form-item label="固定金额">
            <el-switch v-model="ruleWithdrawFixedEnabled" active-text="固定" inactive-text="不固定" />
          </el-form-item>
          <el-form-item v-if="ruleWithdrawFixedEnabled" label="每次提现">
            <el-input-number v-model="ruleForm.withdrawFixedAmount" :min="0.01" :precision="2" />
            <span class="unit">元</span>
          </el-form-item>
        </template>

        <template v-if="[3, 4, 5].includes(ruleForm.ruleMode)">
          <el-divider content-position="left">抽成设置</el-divider>
          <el-form-item label="适用角色">
            <el-select v-model="ruleForm.targetUserRole">
              <el-option label="普通用户" :value="0" :disabled="isCommissionRoleUsed(ruleForm.ruleMode, 0, ruleForm.id)" />
              <el-option label="基础会员" :value="1" :disabled="isCommissionRoleUsed(ruleForm.ruleMode, 1, ruleForm.id)" />
              <el-option label="高级会员" :value="2" :disabled="isCommissionRoleUsed(ruleForm.ruleMode, 2, ruleForm.id)" />
            </el-select>
          </el-form-item>
          <el-form-item label="抽成比例">
            <el-input-number v-model="activeCommissionRate" :min="0" :max="100" :precision="2" />
            <span class="unit">%</span>
          </el-form-item>
        </template>

        <el-form-item label="说明">
          <el-input v-model="ruleForm.ruleDesc" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="ruleVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRuleForm">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="couponForm.id ? '编辑优惠券' : '新增优惠券'" :visible.sync="couponVisible" width="560px">
      <el-form :model="couponForm" label-width="110px">
        <el-form-item label="名称"><el-input v-model="couponForm.templateName" /></el-form-item>
        <el-form-item label="减免金额"><el-input-number v-model="couponForm.discountAmount" :min="0" :precision="2" /></el-form-item>
        <el-form-item label="门槛金额"><el-input-number v-model="couponForm.thresholdAmount" :min="0" :precision="2" /></el-form-item>
        <el-form-item label="有效天数"><el-input-number v-model="couponForm.validDays" :min="1" :precision="0" /></el-form-item>
        <el-form-item label="启用"><el-switch v-model="couponEnabled" /></el-form-item>
        <el-form-item label="备注"><el-input v-model="couponForm.remark" type="textarea" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="couponVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCouponForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  approveDistributionExchange, approveDistributionReward, approveDistributionWithdraw,
  createCouponTemplate, createDistributionRule, getCouponTemplates, getDistributionDashboard,
  getDistributionExchanges, getDistributionRelations, getDistributionRewards, getDistributionRules,
  getDistributionWithdraws, getUserCoupons, markDistributionWithdrawFailed, markDistributionWithdrawPaid,
  rejectDistributionExchange, rejectDistributionReward, rejectDistributionWithdraw, saveCouponTemplate,
  saveDistributionRule, updateDistributionRelation
} from '../../../api/distribution'

const defaultRule = () => ({
  ruleName: '',
  ruleStatus: 1,
  autoAuditEnabled: 0,
  ruleMode: 1,
  inviteTargetCount: 3,
  effectiveConditionType: 1,
  needRegister: 1,
  needRealname: 1,
  needApplication: 1,
  needOrder: 0,
  needMembership: 0,
  perArtistRewardLimit: 0,
  rewardType: 1,
  rewardAmount: 0,
  rewardMembershipDays: 30,
  rewardCouponTemplateId: null,
  rewardExposureValue: 0,
  withdrawMinAmount: 0,
  withdrawFixedAmountEnabled: 0,
  withdrawFixedAmount: 0,
  orderCommissionEnabled: 0,
  orderCommissionRate: 0,
  orderCommissionFixedAmount: 0,
  merchantExposureCommissionEnabled: 0,
  merchantExposureCommissionRate: 0,
  merchantExposureCommissionFixedAmount: 0,
  artistMembershipCommissionEnabled: 0,
  artistMembershipCommissionRate: 0,
  artistMembershipCommissionFixedAmount: 0,
  targetUserRole: 0,
  ruleDesc: ''
})

export default {
  name: 'DistributionManagePage',
  data() {
    return {
      activeTab: 'rules',
      dashboard: {},
      rules: [],
      relations: [],
      relationOptionSource: [],
      rewards: [],
      exchanges: [],
      withdraws: [],
      couponTemplates: [],
      userCoupons: [],
      relationDetailVisible: false,
      relationDetail: {},
      ruleFilters: {
        status: '',
        type: ''
      },
      relationFilters: {
        effectiveStatus: '',
        inviteeSubjectType: '',
        promoterUserId: '',
        inviteeUserId: ''
      },
      rewardFilters: {
        artistName: '',
        type: '',
        status: ''
      },
      exchangeFilters: {
        artistName: '',
        type: '',
        status: ''
      },
      withdrawFilters: {
        artistName: '',
        status: ''
      },
      ruleVisible: false,
      ruleForm: defaultRule(),
      couponVisible: false,
      couponForm: {}
    }
  },
  computed: {
    metrics() {
      return [
        { label: '推广关系', value: this.dashboard.relationCount || 0 },
        { label: '有效邀请', value: this.dashboard.validRelationCount || 0 },
        { label: '待审奖励', value: this.dashboard.pendingRewardCount || 0 },
        { label: '待审兑换', value: this.dashboard.pendingExchangeCount || 0 },
        { label: '待审提现', value: this.dashboard.pendingWithdrawCount || 0 }
      ]
    },
    ruleTypeOptions() {
      return [
        { label: '推N返一', value: 1 },
        { label: '提现规则', value: 2 },
        { label: '订单完成抽成', value: 3 },
        { label: '商家曝光抽成', value: 4 },
        { label: '艺人会员抽成', value: 5 }
      ]
    },
    rewardTypeOptions() {
      return [
        { label: '现金余额', value: 1 },
        { label: '会员时长', value: 2 },
        { label: '优惠券', value: 3 },
        { label: '曝光值', value: 4 }
      ]
    },
    rewardStatusOptions() {
      return this.uniqueStatusOptions(this.rewards)
    },
    exchangeStatusOptions() {
      return this.uniqueStatusOptions(this.exchanges)
    },
    rewardArtistOptions() {
      return this.uniqueAuditArtistOptions(this.rewards)
    },
    exchangeArtistOptions() {
      return this.uniqueAuditArtistOptions(this.exchanges)
    },
    withdrawArtistOptions() {
      return this.uniqueAuditArtistOptions(this.withdraws)
    },
    withdrawStatusOptions() {
      return this.uniqueStatusOptions(this.withdraws)
    },
    filteredRules() {
      return (this.rules || []).filter(item => {
        const statusFilter = this.ruleFilters.status
        const typeFilter = this.ruleFilters.type
        const matchesStatus = statusFilter === '' || statusFilter === null || statusFilter === undefined || Number(item.ruleStatus) === Number(statusFilter)
        const matchesType = typeFilter === '' || typeFilter === null || typeFilter === undefined || this.toRuleMode(item.ruleMode) === Number(typeFilter)
        return matchesStatus && matchesType
      })
    },
    promoterOptions() {
      return this.uniqueRelationOptions(this.relationOptionSource, 'promoter')
    },
    inviteeOptions() {
      const subjectType = this.relationFilters.inviteeSubjectType
      const source = subjectType === '' || subjectType === null || subjectType === undefined
        ? this.relationOptionSource
        : this.relationOptionSource.filter(item => Number(item.inviteeSubjectType) === Number(subjectType))
      return this.uniqueRelationOptions(source, 'invitee')
    },
    filteredRewards() {
      return this.filterAuditItems(this.rewards, this.rewardFilters)
    },
    filteredExchanges() {
      return this.filterAuditItems(this.exchanges, this.exchangeFilters)
    },
    filteredWithdraws() {
      return this.filterAuditItems(this.withdraws, this.withdrawFilters)
    },
    ruleEnabled: { get() { return this.ruleForm.ruleStatus === 2 }, set(v) { this.$set(this.ruleForm, 'ruleStatus', v ? 2 : 1) } },
    ruleAutoAuditEnabled: { get() { return this.ruleForm.autoAuditEnabled === 1 }, set(v) { this.$set(this.ruleForm, 'autoAuditEnabled', v ? 1 : 0) } },
    ruleWithdrawFixedEnabled: { get() { return this.ruleForm.withdrawFixedAmountEnabled === 1 }, set(v) { this.$set(this.ruleForm, 'withdrawFixedAmountEnabled', v ? 1 : 0) } },
    needRegister: { get() { return this.ruleForm.needRegister === 1 }, set(v) { this.$set(this.ruleForm, 'needRegister', v ? 1 : 0) } },
    needRealname: { get() { return this.ruleForm.needRealname === 1 }, set(v) { this.$set(this.ruleForm, 'needRealname', v ? 1 : 0) } },
    needApplication: { get() { return this.ruleForm.needApplication === 1 }, set(v) { this.$set(this.ruleForm, 'needApplication', v ? 1 : 0) } },
    needOrder: { get() { return this.ruleForm.needOrder === 1 }, set(v) { this.$set(this.ruleForm, 'needOrder', v ? 1 : 0) } },
    needMembership: { get() { return this.ruleForm.needMembership === 1 }, set(v) { this.$set(this.ruleForm, 'needMembership', v ? 1 : 0) } },
    rewardLimitMode: {
      get() {
        return Number(this.ruleForm.perArtistRewardLimit || 0) > 0 ? 'limit' : 'repeat'
      },
      set(value) {
        this.$set(this.ruleForm, 'perArtistRewardLimit', value === 'limit' ? 1 : 0)
      }
    },
    activeCommissionRate: {
      get() {
        if (this.ruleForm.ruleMode === 4) return this.ruleForm.merchantExposureCommissionRate
        if (this.ruleForm.ruleMode === 5) return this.ruleForm.artistMembershipCommissionRate
        return this.ruleForm.orderCommissionRate
      },
      set(value) {
        if (this.ruleForm.ruleMode === 4) this.$set(this.ruleForm, 'merchantExposureCommissionRate', value)
        else if (this.ruleForm.ruleMode === 5) this.$set(this.ruleForm, 'artistMembershipCommissionRate', value)
        else this.$set(this.ruleForm, 'orderCommissionRate', value)
      }
    },
    couponEnabled: { get() { return this.couponForm.templateStatus === 1 }, set(v) { this.$set(this.couponForm, 'templateStatus', v ? 1 : 0) } }
  },
  created() {
    this.fetchAll()
  },
  methods: {
    async fetchAll() {
      const [dashboard, rules, relations, rewards, exchanges, withdraws, templates, coupons] = await Promise.all([
        getDistributionDashboard(),
        getDistributionRules(),
        getDistributionRelations(),
        getDistributionRewards(),
        getDistributionExchanges(),
        getDistributionWithdraws(),
        getCouponTemplates(),
        getUserCoupons()
      ])
      this.dashboard = dashboard || {}
      const normalizedTemplates = (templates || []).map(item => ({ ...item, id: String(item.id) }))
      this.couponTemplates = normalizedTemplates
      this.rules = (rules || []).map(item => this.normalizeRule(item, normalizedTemplates))
      this.relations = relations || []
      this.relationOptionSource = relations || []
      this.rewards = rewards || []
      this.exchanges = exchanges || []
      this.withdraws = withdraws || []
      this.userCoupons = coupons || []
    },
    async fetchRelations() {
      this.relations = await getDistributionRelations(this.buildRelationQuery()) || []
    },
    buildRelationQuery() {
      const query = {}
      const filters = this.relationFilters || {}
      ;['effectiveStatus', 'inviteeSubjectType', 'promoterUserId', 'inviteeUserId'].forEach(key => {
        const value = filters[key]
        if (value !== '' && value !== null && value !== undefined) query[key] = value
      })
      return query
    },
    uniqueRelationOptions(source, role) {
      const map = new Map()
      ;(source || []).forEach(item => {
        const userId = role === 'promoter' ? item.promoterUserId : item.inviteeUserId
        if (!userId) return
        const subjectType = role === 'promoter' ? 1 : item.inviteeSubjectType
        const key = `${subjectType || ''}-${userId}`
        if (map.has(key)) return
        const name = role === 'promoter' ? item.promoterName : item.inviteeName
        const mobile = role === 'promoter' ? item.promoterMobile : item.inviteeMobile
        const subject = role === 'promoter' ? '艺人' : (item.inviteeSubjectText || '')
        map.set(key, {
          key,
          value: userId,
          label: [name || userId, subject, mobile].filter(Boolean).join(' / ')
        })
      })
      return Array.from(map.values())
    },
    uniqueStatusOptions(source) {
      const map = new Map()
      ;(source || []).forEach(item => {
        const status = item.status
        if (status === '' || status === null || status === undefined || map.has(Number(status))) return
        map.set(Number(status), {
          label: item.statusText || String(status),
          value: Number(status)
        })
      })
      return Array.from(map.values())
    },
    uniqueAuditArtistOptions(source) {
      const map = new Map()
      ;(source || []).forEach(item => {
        const name = String(item.artistName || '').trim()
        if (!name || map.has(name)) return
        const mobile = item.mobile ? String(item.mobile) : ''
        map.set(name, {
          key: `${name}-${mobile}`,
          value: name,
          label: [name, mobile].filter(Boolean).join(' / ')
        })
      })
      return Array.from(map.values())
    },
    filterAuditItems(source, filters) {
      const nameKeyword = String((filters && filters.artistName) || '').trim().toLowerCase()
      const typeFilter = filters ? filters.type : ''
      const statusFilter = filters ? filters.status : ''
      return (source || []).filter(item => {
        const nameText = String(item.artistName || '').toLowerCase()
        const mobileText = String(item.mobile || '').toLowerCase()
        const matchesName = !nameKeyword || nameText.includes(nameKeyword) || mobileText.includes(nameKeyword)
        const matchesType = typeFilter === '' || typeFilter === null || typeFilter === undefined || Number(item.type) === Number(typeFilter)
        const matchesStatus = statusFilter === '' || statusFilter === null || statusFilter === undefined || Number(item.status) === Number(statusFilter)
        return matchesName && matchesType && matchesStatus
      })
    },
    handleRelationSubjectChange() {
      this.relationFilters.inviteeUserId = ''
      this.fetchRelations()
    },
    resetRelationFilters() {
      this.relationFilters = {
        effectiveStatus: '',
        inviteeSubjectType: '',
        promoterUserId: '',
        inviteeUserId: ''
      }
      this.fetchRelations()
    },
    ruleModeText(mode) {
      const map = { 1: '推N返一', 2: '提现规则', 3: '订单完成抽成', 4: '商家曝光抽成', 5: '艺人会员抽成' }
      return map[this.toRuleMode(mode)] || '推N返一'
    },
    resetRuleFilters() {
      this.ruleFilters = {
        status: '',
        type: ''
      }
    },
    resetRewardFilters() {
      this.rewardFilters = {
        artistName: '',
        type: '',
        status: ''
      }
    },
    resetExchangeFilters() {
      this.exchangeFilters = {
        artistName: '',
        type: '',
        status: ''
      }
    },
    resetWithdrawFilters() {
      this.withdrawFilters = {
        artistName: '',
        status: ''
      }
    },
    commissionRoleText(role) {
      const map = { 0: '普通用户', 1: '基础会员', 2: '高级会员' }
      return map[Number(role || 0)] || '普通用户'
    },
    ruleSummary(row) {
      const mode = this.toRuleMode(row.ruleMode)
      if (mode === 2) return this.withdrawRuleText(row)
      if (mode === 3) return this.commissionLine('', 1, row.orderCommissionRate, row.targetUserRole) || '未配置'
      if (mode === 4) return this.commissionLine('', 1, row.merchantExposureCommissionRate, row.targetUserRole) || '未配置'
      if (mode === 5) return this.commissionLine('', 1, row.artistMembershipCommissionRate, row.targetUserRole) || '未配置'
      return `推 ${row.inviteTargetCount || 0} 返一，${this.rewardText(row)}，${this.rewardLimitText(row)}`
    },
    rewardText(row) {
      if (row.rewardType === 1) return `现金 ${this.money(row.rewardAmount)} 元`
      if (row.rewardType === 2) return `会员 ${row.rewardMembershipDays || 0} 天`
      if (row.rewardType === 3) return '优惠券'
      if (row.rewardType === 4) return `曝光值 ${row.rewardExposureValue || 0}`
      return '-'
    },
    rewardLimitText(row) {
      const limit = Number(row.perArtistRewardLimit || 0)
      return limit > 0 ? `每人最多${limit}次` : '可重复'
    },
    withdrawRuleText(row) {
      const min = Number(row.withdrawMinAmount || 0)
      const fixedEnabled = row.withdrawFixedAmountEnabled === 1
      const fixed = Number(row.withdrawFixedAmount || 0)
      const minText = min > 0 ? `满 ${min.toFixed(2)} 元` : '无门槛'
      return fixedEnabled ? `${minText}，固定 ${fixed.toFixed(2)} 元` : `${minText}，金额不限`
    },
    commissionLine(label, enabled, rate, targetUserRole) {
      if (enabled !== 1) return ''
      const rateNumber = Number(rate || 0)
      const rateText = rateNumber > 0 ? `${rateNumber}%` : '未配置比例'
      return `${label ? `${label} ` : ''}${this.commissionRoleText(targetUserRole)} / ${rateText}`
    },
    findDuplicateCommissionRule(ruleMode, targetUserRole, currentId) {
      const mode = this.toRuleMode(ruleMode)
      if (!this.isCommissionMode(mode)) return null
      const role = Number(targetUserRole || 0)
      const id = currentId === undefined || currentId === null || currentId === '' ? '' : String(currentId)
      return (this.rules || []).find(item => {
        const itemId = item.id === undefined || item.id === null ? '' : String(item.id)
        return this.toRuleMode(item.ruleMode) === mode &&
          Number(item.targetUserRole || 0) === role &&
          (!id || itemId !== id)
      }) || null
    },
    isCommissionRoleUsed(ruleMode, targetUserRole, currentId) {
      return !!this.findDuplicateCommissionRule(ruleMode, targetUserRole, currentId)
    },
    firstAvailableCommissionRole(ruleMode, currentId) {
      return [0, 1, 2].find(role => !this.isCommissionRoleUsed(ruleMode, role, currentId))
    },
    money(value) {
      return Number(value || 0).toFixed(2)
    },
    toRuleMode(value) {
      const mode = Number(value || 1)
      return [1, 2, 3, 4, 5].includes(mode) ? mode : 1
    },
    isCommissionMode(mode) {
      return [3, 4, 5].includes(this.toRuleMode(mode))
    },
    normalizeRule(row, templates = this.couponTemplates) {
      const source = row || {}
      let mode = this.toRuleMode(source.ruleMode)
      if (!source.ruleMode) {
        if (source.withdrawMinAmount > 0 || source.withdrawFixedAmountEnabled === 1) mode = 2
        else if (source.orderCommissionEnabled === 1) mode = 3
        else if (source.merchantExposureCommissionEnabled === 1) mode = 4
        else if (source.artistMembershipCommissionEnabled === 1) mode = 5
      }
      const normalized = { ...defaultRule(), ...source, ruleMode: mode }
      if (normalized.rewardCouponTemplateId) {
        const templateId = String(normalized.rewardCouponTemplateId)
        const template = (templates || []).find(item => String(item.id) === templateId)
        normalized.rewardCouponTemplateId = template ? (template.templateNo || String(template.id)) : templateId
      }
      return normalized
    },
    openRule(row) {
      this.ruleForm = row ? this.normalizeRule(row) : defaultRule()
      this.syncRuleModeDefaults()
      this.ruleVisible = true
    },
    syncRuleModeDefaults() {
      this.$set(this.ruleForm, 'ruleMode', this.toRuleMode(this.ruleForm.ruleMode))
      this.$set(this.ruleForm, 'orderCommissionEnabled', this.ruleForm.ruleMode === 3 ? 1 : 0)
      this.$set(this.ruleForm, 'merchantExposureCommissionEnabled', this.ruleForm.ruleMode === 4 ? 1 : 0)
      this.$set(this.ruleForm, 'artistMembershipCommissionEnabled', this.ruleForm.ruleMode === 5 ? 1 : 0)
      if ([3, 4, 5].includes(this.ruleForm.ruleMode) && ![0, 1, 2].includes(Number(this.ruleForm.targetUserRole))) {
        this.$set(this.ruleForm, 'targetUserRole', 0)
      }
      if ([3, 4, 5].includes(this.ruleForm.ruleMode) && this.isCommissionRoleUsed(this.ruleForm.ruleMode, this.ruleForm.targetUserRole, this.ruleForm.id)) {
        const availableRole = this.firstAvailableCommissionRole(this.ruleForm.ruleMode, this.ruleForm.id)
        if (availableRole !== undefined) this.$set(this.ruleForm, 'targetUserRole', availableRole)
      }
      if (this.ruleForm.ruleMode !== 1 && !this.ruleForm.rewardType) this.$set(this.ruleForm, 'rewardType', 1)
    },
    buildRulePayload() {
      const payload = { ...defaultRule(), ...this.ruleForm, ruleMode: this.toRuleMode(this.ruleForm.ruleMode) }
      payload.orderCommissionEnabled = payload.ruleMode === 3 ? 1 : 0
      payload.merchantExposureCommissionEnabled = payload.ruleMode === 4 ? 1 : 0
      payload.artistMembershipCommissionEnabled = payload.ruleMode === 5 ? 1 : 0
      payload.targetUserRole = [3, 4, 5].includes(payload.ruleMode) ? Number(payload.targetUserRole || 0) : 0
      payload.orderCommissionFixedAmount = 0
      payload.merchantExposureCommissionFixedAmount = 0
      payload.artistMembershipCommissionFixedAmount = 0
      if (payload.rewardType === 3 && payload.rewardCouponTemplateId) payload.rewardCouponTemplateId = String(payload.rewardCouponTemplateId)
      if (payload.rewardType !== 3) payload.rewardCouponTemplateId = null
      if (payload.ruleMode !== 1) {
        payload.inviteTargetCount = 1
        payload.needRegister = 0
        payload.needRealname = 0
        payload.needApplication = 0
        payload.needOrder = 0
        payload.needMembership = 0
        payload.perArtistRewardLimit = 0
        payload.rewardType = 1
        payload.rewardAmount = 0
        payload.rewardMembershipDays = 0
        payload.rewardCouponTemplateId = null
        payload.rewardExposureValue = 0
      }
      if (payload.ruleMode === 2) {
        payload.autoAuditEnabled = 0
      }
      if (payload.ruleMode !== 2) {
        payload.withdrawMinAmount = 0
        payload.withdrawFixedAmountEnabled = 0
        payload.withdrawFixedAmount = 0
      }
      if (payload.ruleMode !== 3) {
        payload.orderCommissionRate = 0
      }
      if (payload.ruleMode !== 4) {
        payload.merchantExposureCommissionRate = 0
      }
      if (payload.ruleMode !== 5) {
        payload.artistMembershipCommissionRate = 0
      }
      return payload
    },
    async saveRuleForm() {
      const payload = this.buildRulePayload()
      if (this.findDuplicateCommissionRule(payload.ruleMode, payload.targetUserRole, payload.id)) {
        this.$message.error('该抽成类型下此角色已存在规则')
        return
      }
      if (payload.id) await saveDistributionRule(payload.id, payload)
      else await createDistributionRule(payload)
      this.$message.success('规则已保存')
      this.ruleVisible = false
      await this.fetchAll()
    },
    openCoupon(row) {
      this.couponForm = row ? { ...row } : { templateName: '', couponType: 1, discountAmount: 0, thresholdAmount: 0, validityType: 1, validDays: 30, useScope: 1, templateStatus: 1, totalQuantity: 0, remark: '' }
      this.couponVisible = true
    },
    async saveCouponForm() {
      if (this.couponForm.id) await saveCouponTemplate(this.couponForm.id, this.couponForm)
      else await createCouponTemplate(this.couponForm)
      this.$message.success('优惠券已保存')
      this.couponVisible = false
      await this.fetchAll()
    },
    async promptRemark(title, required = false) {
      const { value } = await this.$prompt('请输入备注', title, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValidator: v => !required || !!v,
        inputErrorMessage: '请填写原因'
      })
      return { remark: value }
    },
    openRelationDetail(row) {
      this.relationDetail = { ...(row || {}) }
      this.relationDetailVisible = true
    },
    detailText(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    relationStatusTag(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      if (status === 4) return 'danger'
      return 'info'
    },
    effectiveStatusTag(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'info'
    },
    async updateRelationStatus(row, status, title, required = false) {
      await updateDistributionRelation(row.id, status, await this.promptRemark(title, required))
      this.$message.success('推广关系已更新')
      await this.fetchAll()
    },
    async approveReward(row) {
      await approveDistributionReward(row.id, await this.promptRemark('奖励审核通过'))
      this.$message.success('已通过')
      await this.fetchAll()
    },
    async rejectReward(row) {
      await rejectDistributionReward(row.id, await this.promptRemark('奖励驳回', true))
      this.$message.success('已驳回')
      await this.fetchAll()
    },
    async approveExchange(row) {
      await approveDistributionExchange(row.id, await this.promptRemark('兑换审核通过'))
      this.$message.success('已通过')
      await this.fetchAll()
    },
    async rejectExchange(row) {
      await rejectDistributionExchange(row.id, await this.promptRemark('兑换驳回', true))
      this.$message.success('已驳回')
      await this.fetchAll()
    },
    async approveWithdraw(row) {
      await approveDistributionWithdraw(row.id, await this.promptRemark('提现审核通过'))
      this.$message.success('已进入待打款')
      await this.fetchAll()
    },
    async rejectWithdraw(row) {
      await rejectDistributionWithdraw(row.id, await this.promptRemark('提现驳回', true))
      this.$message.success('已驳回')
      await this.fetchAll()
    },
    async markPaid(row) {
      const { value } = await this.$prompt('请输入打款单号或备注', '登记已打款')
      await markDistributionWithdrawPaid(row.id, { paymentNo: value, remark: value })
      this.$message.success('已登记打款')
      await this.fetchAll()
    },
    async markFailed(row) {
      await markDistributionWithdrawFailed(row.id, await this.promptRemark('登记打款失败', true))
      this.$message.success('已登记失败')
      await this.fetchAll()
    }
  }
}
</script>

<style scoped>
.page-head,
.section-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 12px;
  margin: 16px 0;
}
.metric-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2f46;
}
.metric-label {
  margin-top: 6px;
  color: #64748b;
}
.distribution-tabs {
  margin-top: 12px;
}
.section-actions {
  margin-bottom: 12px;
}
.rule-toolbar,
.relation-toolbar,
.audit-toolbar {
  gap: 12px;
}
.rule-filters,
.relation-filters,
.audit-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.rule-filters .el-select {
  width: 150px;
}
.relation-filters .el-select {
  width: 180px;
}
.audit-filters .el-select {
  width: 140px;
}
.audit-filters .el-select:first-child {
  width: 200px;
}
.danger-link {
  color: #f56c6c;
}
.unit {
  margin-left: 8px;
  color: #606266;
}
.rule-effective-alert {
  margin-bottom: 18px;
}
.relation-detail {
  margin-top: 4px;
}
@media (max-width: 960px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }
}
</style>
